import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../lib/api';
import { useToast } from './useToast';
import { PIPELINE_STAGES, PipelineBoard, PipelineStage, STAGE_LABELS } from '../types/pipeline';

const POLL_INTERVAL_MS = 30_000;
// 6s, not 5s: ToastContext bumps action toasts to a 6s minimum lifetime, so the
// commit must fire when the Undo button disappears — otherwise a late click no-ops.
const COMMIT_DELAY_MS = 6_000;

interface UsePipelineBoardReturn {
  board: PipelineBoard | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  moveProject: (projectId: string, toStage: PipelineStage) => void;
}

/** Re-bucket the polled board so pending (optimistic) moves win over poll results. */
function applyPending(
  polled: PipelineBoard,
  pending: Record<string, PipelineStage>
): PipelineBoard {
  if (Object.keys(pending).length === 0) return polled;
  const result = PIPELINE_STAGES.reduce((acc, s) => {
    acc[s] = [];
    return acc;
  }, {} as PipelineBoard);
  for (const stage of PIPELINE_STAGES) {
    for (const p of polled[stage]) {
      const target = pending[p.id] ?? p.pipeline_stage;
      result[target].push(target === p.pipeline_stage ? p : { ...p, pipeline_stage: target });
    }
  }
  return result;
}

/**
 * Fetches the pipeline board, polls every 30s, refetches on tab focus, and
 * applies optimistic drag moves with a 5s deferred commit + Undo. Pending moves
 * overlay the polled board so a poll mid-window never reverts an in-flight drag.
 */
export function usePipelineBoard(): UsePipelineBoardReturn {
  const { showToast } = useToast();
  const [polled, setPolled] = useState<PipelineBoard | null>(null);
  const [pending, setPending] = useState<Record<string, PipelineStage>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingRef = useRef<Record<string, PipelineStage>>({});

  const fetchBoard = useCallback(
    async (opts?: { silent?: boolean }) => {
      try {
        setPolled(await apiClient.getPipelineBoard());
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load pipeline board';
        setError(message);
        if (!opts?.silent) showToast(message, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    void fetchBoard();
    const interval = setInterval(() => void fetchBoard({ silent: true }), POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchBoard({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      // Flush any un-committed moves so a deliberate drag isn't lost on navigation.
      // We intentionally read the LIVE refs at unmount (latest pending), not a
      // mount-time snapshot — the lint auto-fix would defeat that.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const pendingNow = pendingRef.current;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timersNow = timers.current;
      for (const [id, toStage] of Object.entries(pendingNow)) {
        clearTimeout(timersNow[id]);
        // Fire-and-forget on unmount (no UI left to toast); log if the move is lost.
        apiClient.transitionProject(id, toStage).catch(err =>
          console.error('Pending pipeline move lost on unmount', { id, toStage, err })
        );
      }
    };
  }, [fetchBoard]);

  const clearPending = useCallback((projectId: string) => {
    delete pendingRef.current[projectId];
    setPending(prev => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }, []);

  const undoMove = useCallback(
    (projectId: string) => {
      const timer = timers.current[projectId];
      if (!timer) return; // already committed — nothing to undo
      clearTimeout(timer);
      delete timers.current[projectId];
      clearPending(projectId);
    },
    [clearPending]
  );

  const commit = useCallback(
    async (projectId: string, toStage: PipelineStage) => {
      delete timers.current[projectId];
      try {
        await apiClient.transitionProject(projectId, toStage);
        clearPending(projectId);
        void fetchBoard({ silent: true });
      } catch (err) {
        clearPending(projectId);
        showToast(err instanceof Error ? err.message : 'Transition failed', 'error');
      }
    },
    [clearPending, fetchBoard, showToast]
  );

  const moveProject = useCallback(
    (projectId: string, toStage: PipelineStage) => {
      if (timers.current[projectId]) clearTimeout(timers.current[projectId]);
      pendingRef.current[projectId] = toStage;
      setPending(prev => ({ ...prev, [projectId]: toStage }));
      timers.current[projectId] = setTimeout(() => void commit(projectId, toStage), COMMIT_DELAY_MS);
      showToast(`Moved to ${STAGE_LABELS[toStage]}`, {
        type: 'success',
        duration: COMMIT_DELAY_MS,
        action: { label: 'Undo', onClick: () => undoMove(projectId) },
      });
    },
    [commit, showToast, undoMove]
  );

  const board = useMemo(() => (polled ? applyPending(polled, pending) : null), [polled, pending]);
  const refetch = useCallback(() => fetchBoard(), [fetchBoard]);

  return { board, isLoading, error, refetch, moveProject };
}
